import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import useIsMobile from '../../hooks/useIsMobile';
import { helpCenterService } from '../../services/api';
import { HELP_CATEGORIES, findArticleById, searchArticles } from '../../data/helpContent';
import { C } from '../../styles/theme';

const FONT = "'Plus Jakarta Sans', sans-serif";

const CATEGORY_ICONS = {
    rocket: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
            <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
            <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
            <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
        </svg>
    ),
    monitor: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
        </svg>
    ),
    users: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
    ),
    package: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
    ),
    wallet: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <line x1="2" y1="10" x2="22" y2="10"/>
        </svg>
    ),
    chart: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
    ),
};

const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
);

const ChevronRight = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);

const TipIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18h6"/>
        <path d="M10 22h4"/>
        <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
    </svg>
);

const WarningIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.amber} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const Breadcrumb = ({ items, onNavigate }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, fontSize: 13, color: C.muted }}>
        {items.map((item, i) => (
            <React.Fragment key={i}>
                {i > 0 && <ChevronRight />}
                {item.onClick ? (
                    <span
                        onClick={item.onClick}
                        style={{ cursor: 'pointer', color: C.green, fontWeight: 500 }}
                        onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={e => e.target.style.textDecoration = 'none'}
                    >
                        {item.label}
                    </span>
                ) : (
                    <span style={{ color: C.text, fontWeight: 600 }}>{item.label}</span>
                )}
            </React.Fragment>
        ))}
    </div>
);


const STATUS_COLORS = {
    open: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', label: 'Open' },
    in_progress: { bg: '#fffbeb', color: '#d97706', border: '#fde68a', label: 'In Progress' },
    resolved: { bg: '#eff5ff', color: '#2563eb', border: '#bfdbfe', label: 'Resolved' },
};

const PortalHelpCenterPage = () => {
    const { isSuperAdmin } = useAuth();
    const { t, language } = useLanguage();
    const isMobile = useIsMobile();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('guides');
    const [view, setView] = useState('home');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedScenarios, setExpandedScenarios] = useState({});
    const [feedback, setFeedback] = useState({});
    const [lightboxImage, setLightboxImage] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketFilter, setTicketFilter] = useState('all');
    const isRTL = language === 'ar';

    useEffect(() => {
        if (isSuperAdmin && activeTab === 'tickets') {
            setTicketsLoading(true);
            helpCenterService.getTickets()
                .then(setTickets)
                .catch(() => {})
                .finally(() => setTicketsLoading(false));
        }
    }, [isSuperAdmin, activeTab]);

    useEffect(() => {
        const articleParam = searchParams.get('article');
        if (articleParam) {
            const result = findArticleById(articleParam);
            if (result) {
                setSelectedCategory(result.category);
                setSelectedArticle(result.article);
                setView('article');
                setSearchParams({});
            }
        }
    }, [searchParams, setSearchParams]);

    const searchResults = useMemo(() => {
        if (!searchQuery || searchQuery.length < 2) return [];
        return searchArticles(searchQuery, t);
    }, [searchQuery, t]);

    const navigateHome = () => {
        setView('home');
        setSelectedCategory(null);
        setSelectedArticle(null);
        setSearchQuery('');
    };

    const navigateCategory = (cat) => {
        setView('category');
        setSelectedCategory(cat);
        setSelectedArticle(null);
        setSearchQuery('');
    };

    const navigateArticle = (cat, article) => {
        setView('article');
        setSelectedCategory(cat);
        setSelectedArticle(article);
        setSearchQuery('');
        setExpandedScenarios({});
    };

    const toggleScenario = (idx) => {
        setExpandedScenarios(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const handleFeedback = (articleId, helpful) => {
        setFeedback(prev => ({ ...prev, [articleId]: helpful }));
    };

    // ─── Home View ──────────────────────────────────────────────────────────
    const renderHome = () => (
        <div>
            <div style={{ textAlign: 'center', marginBottom: 40 }}>
                <h1 style={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, color: C.text, margin: 0, marginBottom: 8 }}>
                    {t('help_center_title')}
                </h1>
                <p style={{ fontFamily: FONT, fontSize: 14, color: C.muted, margin: 0, marginBottom: 24 }}>
                    {t('help_center_subtitle')}
                </p>

                <div style={{ position: 'relative', maxWidth: 500, margin: '0 auto' }}>
                    <div style={{ position: 'absolute', [isRTL ? 'right' : 'left']: 14, top: '50%', transform: 'translateY(-50%)' }}>
                        <SearchIcon />
                    </div>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder={t('help_search_placeholder')}
                        style={{
                            width: '100%', padding: '12px 16px', [isRTL ? 'paddingRight' : 'paddingLeft']: 42,
                            border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 14,
                            fontFamily: FONT, color: C.text, background: C.white, outline: 'none',
                            boxSizing: 'border-box', direction: isRTL ? 'rtl' : 'ltr',
                        }}
                        onFocus={e => e.target.style.borderColor = C.green}
                        onBlur={e => e.target.style.borderColor = C.border}
                    />
                </div>
            </div>

            {searchQuery.length >= 2 ? (
                <div>
                    <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginBottom: 16 }}>
                        {searchResults.length} {t('help_results_found')}
                    </p>
                    {searchResults.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: C.muted, fontFamily: FONT }}>
                            <p style={{ fontSize: 14 }}>{t('help_no_results')}</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {searchResults.map(({ category, article }) => (
                                <div
                                    key={article.id}
                                    onClick={() => navigateArticle(category, article)}
                                    style={{
                                        padding: '14px 18px', background: C.white, border: `1px solid ${C.border}`,
                                        borderRadius: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                                        alignItems: 'center', transition: 'border-color 0.15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.borderColor = C.green}
                                    onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                                >
                                    <div>
                                        <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: C.text }}>
                                            {t(article.titleKey)}
                                        </div>
                                        <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: 2 }}>
                                            {t(category.titleKey)}
                                        </div>
                                    </div>
                                    <ChevronRight />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 16,
                }}>
                    {HELP_CATEGORIES.map(cat => (
                        <div
                            key={cat.id}
                            onClick={() => navigateCategory(cat)}
                            style={{
                                padding: 24, background: C.white, border: `1px solid ${C.border}`,
                                borderRadius: 12, cursor: 'pointer', transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.borderColor = C.green;
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(37, 99, 235,0.08)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.borderColor = C.border;
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div style={{
                                width: 48, height: 48, borderRadius: 10, background: C.greenLight,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: C.green, marginBottom: 14,
                            }}>
                                {CATEGORY_ICONS[cat.icon]}
                            </div>
                            <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                                {t(cat.titleKey)}
                            </div>
                            <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
                                {t(cat.descriptionKey)}
                            </div>
                            <div style={{ fontFamily: FONT, fontSize: 12, color: C.mutedLight }}>
                                {cat.articles.length} {t('help_articles_count')}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div style={{
                padding: '20px 24px', background: C.greenLight, borderRadius: 10,
                border: `1px solid ${C.greenBorder}`, textAlign: 'center', marginTop: 32,
            }}>
                <p style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: C.green, margin: 0, marginBottom: 4 }}>
                    {t('help_cant_find')}
                </p>
                <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: 0, marginBottom: 12 }}>
                    {t('help_cant_find_desc')}
                </p>
                <button
                    onClick={() => window.dispatchEvent(new CustomEvent('open-help-chat'))}
                    style={{
                        padding: '10px 24px', background: C.green, color: C.white, border: 'none',
                        borderRadius: 8, fontSize: 14, fontWeight: 600, fontFamily: FONT,
                        cursor: 'pointer', transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.target.style.background = C.greenDark}
                    onMouseLeave={e => e.target.style.background = C.green}
                >
                    {t('help_chat_with_assistant')}
                </button>
            </div>
        </div>
    );

    // ─── Category View ──────────────────────────────────────────────────────
    const renderCategory = () => {
        if (!selectedCategory) return null;
        return (
            <div>
                <Breadcrumb items={[
                    { label: t('help_center_title'), onClick: navigateHome },
                    { label: t(selectedCategory.titleKey) },
                ]} />
                <h2 style={{ fontFamily: FONT, fontSize: 22, fontWeight: 800, color: C.text, margin: 0, marginBottom: 8 }}>
                    {t(selectedCategory.titleKey)}
                </h2>
                <p style={{ fontFamily: FONT, fontSize: 14, color: C.muted, marginBottom: 24 }}>
                    {t(selectedCategory.descriptionKey)}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selectedCategory.articles.map(article => (
                        <div
                            key={article.id}
                            onClick={() => navigateArticle(selectedCategory, article)}
                            style={{
                                padding: '16px 20px', background: C.white, border: `1px solid ${C.border}`,
                                borderRadius: 10, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                                alignItems: 'center', transition: 'border-color 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.borderColor = C.green}
                            onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                        >
                            <div>
                                <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: C.text }}>
                                    {t(article.titleKey)}
                                </div>
                                <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, marginTop: 4, lineHeight: 1.4 }}>
                                    {t(article.summaryKey)}
                                </div>
                            </div>
                            <ChevronRight />
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // ─── Article View ───────────────────────────────────────────────────────
    const renderArticle = () => {
        if (!selectedArticle || !selectedCategory) return null;
        return (
            <div style={{ display: 'flex', gap: isMobile ? 0 : 40 }}>
            <div style={{ flex: 1, maxWidth: 720, minWidth: 0 }}>
                <Breadcrumb items={[
                    { label: t('help_center_title'), onClick: navigateHome },
                    { label: t(selectedCategory.titleKey), onClick: () => navigateCategory(selectedCategory) },
                    { label: t(selectedArticle.titleKey) },
                ]} />

                <h1 style={{ fontFamily: FONT, fontSize: 24, fontWeight: 800, color: C.text, margin: 0, marginBottom: 8 }}>
                    {t(selectedArticle.titleKey)}
                </h1>
                <p style={{ fontFamily: FONT, fontSize: 14, color: C.muted, lineHeight: 1.6, marginBottom: 32 }}>
                    {t(selectedArticle.summaryKey)}
                </p>

                {selectedArticle.sections.map((section, i) => (
                    <div key={i} id={`section-${i}`} style={{ marginBottom: 28, scrollMarginTop: 20 }}>
                        <h3 style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: C.text, margin: 0, marginBottom: 10 }}>
                            {t(section.headingKey)}
                        </h3>
                        <p style={{ fontFamily: FONT, fontSize: 14, color: '#475569', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-line' }}>
                            {t(section.contentKey)}
                        </p>
                        {section.image && (
                            <div
                                onClick={() => setLightboxImage(section.image)}
                                style={{
                                    marginTop: 16, borderRadius: 10, overflow: 'hidden',
                                    border: `1px solid ${C.border}`,
                                    maxWidth: section.imageMaxWidth || '100%',
                                    maxHeight: section.imageMaxHeight || 400,
                                    width: 'fit-content', cursor: 'zoom-in',
                                    transition: 'box-shadow 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.1)'}
                                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                            >
                                <img
                                    src={section.image}
                                    alt={t(section.headingKey)}
                                    style={{ width: '100%', display: 'block' }}
                                />
                            </div>
                        )}
                    </div>
                ))}

                {selectedArticle.tips.length > 0 && (
                    <div id="section-tips" style={{ marginBottom: 24, scrollMarginTop: 20 }}>
                        {selectedArticle.tips.map((tip, i) => (
                            <div key={i} style={{
                                display: 'flex', gap: 10, padding: '12px 16px', background: C.greenLight,
                                borderRadius: 8, marginBottom: 8, border: `1px solid ${C.greenBorder}`,
                            }}>
                                <div style={{ flexShrink: 0, marginTop: 1 }}><TipIcon /></div>
                                <p style={{ fontFamily: FONT, fontSize: 13, color: C.greenDark, lineHeight: 1.5, margin: 0 }}>
                                    {t(tip.textKey)}
                                </p>
                            </div>
                        ))}
                    </div>
                )}

                {selectedArticle.warnings.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                        {selectedArticle.warnings.map((warn, i) => (
                            <div key={i} style={{
                                display: 'flex', gap: 10, padding: '12px 16px', background: C.amberLight,
                                borderRadius: 8, marginBottom: 8, border: `1px solid ${C.amberBorder}`,
                            }}>
                                <div style={{ flexShrink: 0, marginTop: 1 }}><WarningIcon /></div>
                                <p style={{ fontFamily: FONT, fontSize: 13, color: '#92400e', lineHeight: 1.5, margin: 0 }}>
                                    {t(warn.textKey)}
                                </p>
                            </div>
                        ))}
                    </div>
                )}

                {selectedArticle.scenarios.length > 0 && (
                    <div id="section-scenarios" style={{ marginBottom: 32, scrollMarginTop: 20 }}>
                        <h3 style={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 12 }}>
                            {t('help_what_if')}
                        </h3>
                        {selectedArticle.scenarios.map((scenario, i) => (
                            <div key={i} style={{
                                border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 8,
                                overflow: 'hidden', background: C.white,
                            }}>
                                <div
                                    onClick={() => toggleScenario(i)}
                                    style={{
                                        padding: '12px 16px', cursor: 'pointer', display: 'flex',
                                        justifyContent: 'space-between', alignItems: 'center',
                                    }}
                                >
                                    <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text }}>
                                        {t(scenario.questionKey)}
                                    </span>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2"
                                        style={{ transform: expandedScenarios[i] ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                                        <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                </div>
                                {expandedScenarios[i] && (
                                    <div style={{ padding: '0 16px 14px', borderTop: `1px solid ${C.border}` }}>
                                        <p style={{ fontFamily: FONT, fontSize: 13, color: '#475569', lineHeight: 1.6, margin: 0, marginTop: 12 }}>
                                            {t(scenario.answerKey)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div style={{
                    padding: '16px 20px', background: C.bg, borderRadius: 10, border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24,
                }}>
                    <span style={{ fontFamily: FONT, fontSize: 13, color: C.muted }}>
                        {t('help_was_helpful')}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {['yes', 'no'].map(val => (
                            <button
                                key={val}
                                onClick={() => handleFeedback(selectedArticle.id, val)}
                                style={{
                                    padding: '6px 16px', borderRadius: 6, fontSize: 13, fontFamily: FONT, fontWeight: 600,
                                    border: `1px solid ${feedback[selectedArticle.id] === val ? C.green : C.border}`,
                                    background: feedback[selectedArticle.id] === val ? C.greenLight : C.white,
                                    color: feedback[selectedArticle.id] === val ? C.green : C.muted,
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                {val === 'yes' ? t('help_yes') : t('help_no')}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{
                    padding: '20px 24px', background: C.greenLight, borderRadius: 10,
                    border: `1px solid ${C.greenBorder}`, textAlign: 'center',
                }}>
                    <p style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: C.green, margin: 0, marginBottom: 4 }}>
                        {t('help_cant_find')}
                    </p>
                    <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: 0, marginBottom: 12 }}>
                        {t('help_cant_find_desc')}
                    </p>
                    <button
                        onClick={() => window.dispatchEvent(new CustomEvent('open-help-chat'))}
                        style={{
                            padding: '10px 24px', background: C.green, color: C.white, border: 'none',
                            borderRadius: 8, fontSize: 14, fontWeight: 600, fontFamily: FONT,
                            cursor: 'pointer', transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.target.style.background = C.greenDark}
                        onMouseLeave={e => e.target.style.background = C.green}
                    >
                        {t('help_chat_with_assistant')}
                    </button>
                </div>
            </div>

            {/* Table of Contents sidebar — hidden on mobile (narrow screens) */}
            {!isMobile && (
            <div style={{
                width: 200, flexShrink: 0, position: 'sticky', top: 0, alignSelf: 'flex-start',
                paddingTop: 60,
            }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 12 }}>
                    {t('help_on_this_page')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedArticle.sections.map((section, i) => (
                        <div
                            key={i}
                            onClick={() => {
                                const el = document.getElementById(`section-${i}`);
                                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                            style={{
                                fontFamily: FONT, fontSize: 12, color: C.muted, padding: '5px 0',
                                cursor: 'pointer', borderLeft: `2px solid ${C.border}`,
                                paddingLeft: 12, transition: 'all 0.15s', lineHeight: 1.4,
                            }}
                            onMouseEnter={e => { e.currentTarget.style.color = C.green; e.currentTarget.style.borderLeftColor = C.green; }}
                            onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.borderLeftColor = C.border; }}
                        >
                            {t(section.headingKey)}
                        </div>
                    ))}
                    {selectedArticle.tips.length > 0 && (
                        <div
                            onClick={() => { const el = document.getElementById('section-tips'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                            style={{ fontFamily: FONT, fontSize: 12, color: C.muted, padding: '5px 0', cursor: 'pointer', borderLeft: `2px solid ${C.border}`, paddingLeft: 12, transition: 'all 0.15s', lineHeight: 1.4 }}
                            onMouseEnter={e => { e.currentTarget.style.color = C.green; e.currentTarget.style.borderLeftColor = C.green; }}
                            onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.borderLeftColor = C.border; }}
                        >
                            Tips
                        </div>
                    )}
                    {selectedArticle.scenarios.length > 0 && (
                        <div
                            onClick={() => { const el = document.getElementById('section-scenarios'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                            style={{ fontFamily: FONT, fontSize: 12, color: C.muted, padding: '5px 0', cursor: 'pointer', borderLeft: `2px solid ${C.border}`, paddingLeft: 12, transition: 'all 0.15s', lineHeight: 1.4 }}
                            onMouseEnter={e => { e.currentTarget.style.color = C.green; e.currentTarget.style.borderLeftColor = C.green; }}
                            onMouseLeave={e => { e.currentTarget.style.color = C.muted; e.currentTarget.style.borderLeftColor = C.border; }}
                        >
                            {t('help_what_if')}
                        </div>
                    )}
                </div>
            </div>
            )}
            </div>
        );
    };

    const updateTicketStatus = async (ticketId, newStatus) => {
        try {
            await helpCenterService.updateTicket(ticketId, newStatus);
            setTickets(prev => prev.map(t =>
                t.ticket_id === ticketId
                    ? { ...t, status: newStatus, resolved_at: newStatus === 'resolved' ? new Date().toISOString() : t.resolved_at }
                    : t
            ));
            if (selectedTicket?.ticket_id === ticketId) {
                setSelectedTicket(prev => ({ ...prev, status: newStatus }));
            }
        } catch {}
    };

    const filteredTickets = ticketFilter === 'all' ? tickets : tickets.filter(t => t.status === ticketFilter);

    // ─── Admin Inbox ────────────────────────────────────────────────────────
    const renderTickets = () => {
        if (selectedTicket) {
            const sc = STATUS_COLORS[selectedTicket.status] || STATUS_COLORS.open;
            return (
                <div>
                    <div
                        onClick={() => setSelectedTicket(null)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: C.green, fontSize: 13, fontWeight: 600, marginBottom: 20 }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                        {t('help_back_to_tickets')}
                    </div>

                    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                            <div>
                                <h2 style={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: C.text, margin: 0, marginBottom: 6 }}>
                                    {selectedTicket.tenant_name}
                                </h2>
                                <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: 0 }}>
                                    {selectedTicket.employee_name} ({selectedTicket.employee_role || 'N/A'})
                                </p>
                            </div>
                            <span style={{
                                padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                                background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                            }}>
                                {sc.label}
                            </span>
                        </div>

                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 8 }}>
                                {t('help_ticket_summary')}
                            </div>
                            <p style={{ fontFamily: FONT, fontSize: 14, color: C.text, lineHeight: 1.6, margin: 0, padding: 16, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                                {selectedTicket.summary}
                            </p>
                        </div>

                        <div style={{ marginBottom: 20 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 8 }}>
                                {t('help_chat_transcript')}
                            </div>
                            <div style={{ maxHeight: 300, overflowY: 'auto', padding: 16, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                                {(selectedTicket.chat_transcript || []).map((msg, i) => (
                                    <div key={i} style={{ marginBottom: 10 }}>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: msg.role === 'user' ? C.blue : C.green }}>
                                            {msg.role === 'user' ? selectedTicket.employee_name : 'Aqana Assistant'}
                                        </span>
                                        <p style={{ fontFamily: FONT, fontSize: 13, color: C.text, lineHeight: 1.5, margin: '2px 0 0' }}>
                                            {msg.content}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8 }}>
                            {selectedTicket.status !== 'in_progress' && selectedTicket.status !== 'resolved' && (
                                <button onClick={() => updateTicketStatus(selectedTicket.ticket_id, 'in_progress')}
                                    style={{ padding: '8px 16px', background: C.amberLight, color: C.amber, border: `1px solid ${C.amberBorder}`, borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: 'pointer' }}>
                                    {t('help_mark_in_progress')}
                                </button>
                            )}
                            {selectedTicket.status !== 'resolved' && (
                                <button onClick={() => updateTicketStatus(selectedTicket.ticket_id, 'resolved')}
                                    style={{ padding: '8px 16px', background: C.greenLight, color: C.green, border: `1px solid ${C.greenBorder}`, borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: 'pointer' }}>
                                    {t('help_mark_resolved')}
                                </button>
                            )}
                        </div>

                        <div style={{ marginTop: 16, fontSize: 12, color: C.mutedLight }}>
                            {t('help_created_at')}: {new Date(selectedTicket.created_at).toLocaleString()}
                            {selectedTicket.resolved_at && (
                                <span> | {t('help_resolved_at')}: {new Date(selectedTicket.resolved_at).toLocaleString()}</span>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 20, ...(isMobile ? { overflowX: 'auto', whiteSpace: 'nowrap', flexWrap: 'nowrap', paddingBottom: 4 } : {}) }}>
                    {['all', 'open', 'in_progress', 'resolved'].map(f => (
                        <button key={f} onClick={() => setTicketFilter(f)}
                            style={{
                                padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, flexShrink: 0,
                                fontFamily: FONT, cursor: 'pointer', border: `1px solid ${ticketFilter === f ? C.green : C.border}`,
                                background: ticketFilter === f ? C.greenLight : C.white,
                                color: ticketFilter === f ? C.green : C.muted,
                            }}>
                            {f === 'all' ? t('help_filter_all') : (STATUS_COLORS[f]?.label || f)}
                        </button>
                    ))}
                </div>

                {ticketsLoading ? (
                    <div style={{ textAlign: 'center', padding: 40, color: C.muted, fontFamily: FONT }}>
                        {t('help_loading_tickets')}
                    </div>
                ) : filteredTickets.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="1.5" style={{ marginBottom: 12 }}>
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p style={{ fontFamily: FONT, fontSize: 14, color: C.muted }}>{t('help_no_tickets')}</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {filteredTickets.map(ticket => {
                            const sc = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;
                            return (
                                <div key={ticket.ticket_id}
                                    onClick={() => setSelectedTicket(ticket)}
                                    style={{
                                        padding: '14px 18px', background: C.white, border: `1px solid ${C.border}`,
                                        borderRadius: 10, cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                                        alignItems: 'center', transition: 'border-color 0.15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.borderColor = C.green}
                                    onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                                >
                                    <div style={{ flex: 1, overflow: 'hidden' }}>
                                        <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 2 }}>
                                            {ticket.tenant_name}
                                        </div>
                                        <div style={{
                                            fontFamily: FONT, fontSize: 13, color: C.muted, whiteSpace: 'nowrap',
                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}>
                                            {ticket.summary}
                                        </div>
                                        <div style={{ fontFamily: FONT, fontSize: 11, color: C.mutedLight, marginTop: 4 }}>
                                            {ticket.employee_name} · {new Date(ticket.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <span style={{
                                        padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700,
                                        background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                                        whiteSpace: 'nowrap', marginLeft: 12,
                                    }}>
                                        {sc.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <PortalLayout>
            <div style={{ fontFamily: FONT, padding: '0 4px', direction: isRTL ? 'rtl' : 'ltr' }}>
                {isSuperAdmin && (
                    <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.border}`, paddingBottom: 0, ...(isMobile ? { overflowX: 'auto', whiteSpace: 'nowrap', flexWrap: 'nowrap' } : {}) }}>
                        {['guides', 'tickets'].map(tab => (
                            <button key={tab} onClick={() => { setActiveTab(tab); if (tab === 'guides') navigateHome(); }}
                                style={{
                                    padding: '10px 20px', fontSize: 13, fontWeight: 600, fontFamily: FONT,
                                    border: 'none', background: 'none', cursor: 'pointer',
                                    color: activeTab === tab ? C.green : C.muted,
                                    borderBottom: `2px solid ${activeTab === tab ? C.green : 'transparent'}`,
                                    marginBottom: -1, transition: 'all 0.15s',
                                }}>
                                {tab === 'guides' ? t('help_tab_guides') : t('help_tab_tickets')}
                            </button>
                        ))}
                    </div>
                )}

                {activeTab === 'guides' ? (
                    <>
                        {view === 'home' && renderHome()}
                        {view === 'category' && renderCategory()}
                        {view === 'article' && renderArticle()}
                    </>
                ) : (
                    renderTickets()
                )}
            </div>

            {lightboxImage && (
                <div
                    onClick={() => setLightboxImage(null)}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'zoom-out', padding: 40,
                    }}
                >
                    <img
                        src={lightboxImage}
                        alt=""
                        style={{ maxWidth: '90%', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.3)' }}
                        onClick={e => e.stopPropagation()}
                    />
                    <div
                        onClick={() => setLightboxImage(null)}
                        style={{
                            position: 'absolute', top: 20, right: 20, width: 36, height: 36,
                            borderRadius: '50%', background: 'rgba(255,255,255,0.15)', color: '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontSize: 18,
                        }}
                    >
                        &times;
                    </div>
                </div>
            )}
        </PortalLayout>
    );
};

export default PortalHelpCenterPage;
